import { Injectable } from '@angular/core';
import { AuthService } from '@app/services';
import { Employee } from '@models/employee/employee.model';
import { EmployeeBenefit } from '@models/payroll/employee/benefit.model';

@Injectable()
export class EmployeeIndexService {
    assignedEmployees: Employee[] = [];

    constructor(public auth: AuthService) {}

    /**
     * @todo This logic is similar to the logic in the base.model
     * However because this is payroll, we are not dealing with real employee relationships,
     */
    determineAttachAndDetach(selected: Employee[], existing: Employee[]): [number[], number[]] {
        const newSelectionIds = selected.map((i) => i.prEmployeeId);

        const detach: number[] = existing
            .filter((e: Employee) => !newSelectionIds.includes(e.prEmployeeId))
            .map((e: Employee) => e.prEmployeeId);

        const existingIds = existing.map((i) => i.prEmployeeId);

        const attach: number[] = selected
            .filter((e: Employee) => !existingIds.includes(e.prEmployeeId))
            .map((e: Employee) => e.prEmployeeId);

        return [attach, detach];
    }

    /**
     * @todo the bulk select component currently takes a list of all selected employees
     * This defeats the purpose of paginating the data elsewhere, should be corrected
     * when we rework that component
     *
     * @bug the previous implementation of this was sending every employee ID to select
     * which was resulting in massive GET requests that could be so large they couldn't
     * be sent. This could be getting stopped by the browser vendor if the request are
     * too large.
     * https://www.geeksforgeeks.org/maximum-length-of-a-url-in-different-browsers/
     * Chrome: 2mb
     * Firefox: 65.5kb
     * Safari: 80kb
     * Nginx: configuration specific
     *
     * Instead, fetch all employees and filter them out in memory. Use a Set to make filtering
     * O(n) [O(1) to find employee in set, O(n) to iterate list of employees].
     *
     * @bug this and apps/hr-angular/src/app/modules/payroll/views/deduction/employee/index/employee-index.service.ts
     * share basically the same code and should be refactored to take in a pre-built query. The only value that this
     * service cares about from the API is employeeId
     *
     * https://gethumi.atlassian.net/browse/PRE-1781
     */
    async loadAllAssigned(benefitId: number): Promise<void> {
        this.assignedEmployees = [];
        let assignedPrEmployeeIds: Set<number> = new Set();
        let currentPage = 1;
        let total = 1;
        let loaded = 0;

        while (loaded < total) {
            const [employeeBenefits, meta] = await EmployeeBenefit.param('company', this.auth.company.id)
                .param('benefit', benefitId)
                .with('employee')
                .page(currentPage)
                .get();

            employeeBenefits.forEach((benefit) => assignedPrEmployeeIds.add(benefit.employeeId));

            total = meta.pagination.total;
            loaded += meta.pagination.perPage;
            currentPage = currentPage + 1;
        }

        const [employees] = await Employee.param('company', this.auth.company.id)
            .whereIn('status', ['active', 'terminated', 'onboarding', 'on leave'])
            .where('onPayroll', 1)
            .all();

        employees.forEach((employee) => {
            if (assignedPrEmployeeIds.has(employee.prEmployeeId)) {
                this.assignedEmployees.push(employee);
            }
        });
    }
}
